"use client";

import { c as _c } from "react/compiler-runtime";
import { resolveMegaMenuLogoDisplayHeightPx, resolveMegaMenuLogoRasterMaxPx } from "@multi-tenant-cms/builder/ui/lib/megaMenuHeaderLogo";
import { isReplicaChrome } from "@multi-tenant-cms/builder/ui/lib/replicaMegaMenuHeader";
import type { AdvancedMegaMenuHeaderActionLink, AdvancedMegaMenuHeaderRuntimePayload, AdvancedMegaMenuNavIcon, AdvancedMegaMenuPromoCard } from "@multi-tenant-cms/types/menu";
import { type MouseEvent as ReactMouseEvent, type ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AdvancedMegaMenuReplicaChrome } from "./AdvancedMegaMenuReplicaChrome";
import styles from "./advanced-mega-menu-header.module.css";
import { applyAdvancedMegaMenuDesignCssVars } from "./advancedMegaMenuDesignCssVars";
import { sanitizeIconifySvgMarkup } from "./iconifySvg";
import { imgElementIntrinsicProps } from "./mediaResponsive";
import { isLocaleCurrentForPath, resolveLocaleCodeFromPath } from "./megaMenuLocale";
import { consumeOpenMegaTabLabel, findMegaTabByLabel } from "./megaMenuOpenTabRestore";
import { useMegaMenuStickyHeader } from "./useMegaMenuStickyHeader";

/** Avoid using image `src` or media API paths as the logo link target. */
function safeLogoHref(logoHref: string | undefined, logoUrl?: string): string {
  const href = (logoHref ?? "/").trim() || "/";
  const url = logoUrl?.trim();
  if (url && href === url) {
    return "/";
  }
  if (/\/api\/media\//i.test(href)) {
    return "/";
  }
  if (/^https?:\/\/\S+\.(png|jpe?g|gif|webp|svg|avif)(\?|#|$)/i.test(href)) {
    return "/";
  }
  return href;
}
type MegaTabRoot = Extract<AdvancedMegaMenuHeaderRuntimePayload["roots"][number], {
  itemKind: "mega_tab";
}>;
type ViewportMode = "mobile" | "tablet" | "desktop";
type MenuRoot = AdvancedMegaMenuHeaderRuntimePayload["roots"][number];
function menuRootKey(root: MenuRoot, index: number): string {
  if (root.id) {
    return root.id;
  }
  return `root-${root.itemKind}-${root.sortOrder}-${index}`;
}
const MOBILE_MAX = 767;
const TABLET_MAX = 1023;

/** String literals satisfy static a11y analyzers (e.g. Edge Tools axe) that cannot evaluate JSX booleans. */
const ARIA_EXPANDED_TRUE = {
  "aria-expanded": "true"
} as const;
const ARIA_EXPANDED_FALSE = {
  "aria-expanded": "false"
} as const;
const ARIA_HIDDEN_TRUE = {
  "aria-hidden": "true"
} as const;
const ARIA_HIDDEN_FALSE = {
  "aria-hidden": "false"
} as const;

/** Mega tab roots that open a flyout panel (groups, promo cards, and/or footer pills). */
function megaTabHasPanelContent(root: MegaTabRoot, promos: AdvancedMegaMenuHeaderRuntimePayload["promos"], footerRows?: AdvancedMegaMenuHeaderRuntimePayload["footerRows"]): boolean {
  if (root.groups.some(group => group.links.length > 0)) {
    return true;
  }
  if ((promos[root.id]?.length ?? 0) > 0) {
    return true;
  }
  const footer = footerRows?.[root.id];
  return footer != null && footer.enabled !== false && footer.links.some(l => l.label.trim().length > 0);
}
function resolveColumnCount(root: MegaTabRoot): 2 | 3 | 4 {
  if (root.columnCount === 2 || root.columnCount === 3 || root.columnCount === 4) {
    return root.columnCount;
  }
  let maxIndex = 0;
  for (const g of root.groups) {
    if (g.columnIndex > maxIndex) {
      maxIndex = g.columnIndex;
    }
  }
  const derived = maxIndex + 1;
  if (derived <= 2) {
    return 2;
  }
  if (derived >= 4) {
    return 4;
  }
  return 3;
}
function groupsByColumn(groups: MegaTabRoot["groups"], columnCount: 2 | 3 | 4): Array<MegaTabRoot["groups"]> {
  const cols: Array<MegaTabRoot["groups"]> = Array.from({
    length: columnCount
  }, () => []);
  for (const g of groups) {
    const i = Math.min(columnCount - 1, Math.max(0, g.columnIndex));
    cols[i].push(g);
  }
  return cols;
}
function useViewportMode(forced) {
  const $ = _c(3);
  const [mode, setMode] = useState("desktop");
  let t0;
  let t1;
  if ($[0] !== forced) {
    t0 = () => {
      if (forced === "mobile" || forced === "tablet" || forced === "desktop") {
        return;
      }
      const update = () => {
        const w = window.innerWidth;
        if (w <= MOBILE_MAX) {
          setMode("mobile");
        } else {
          if (w <= TABLET_MAX) {
            setMode("tablet");
          } else {
            setMode("desktop");
          }
        }
      };
      update();
      window.addEventListener("resize", update);
      return () => window.removeEventListener("resize", update);
    };
    t1 = [forced];
    $[0] = forced;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  if (forced === "mobile" || forced === "tablet" || forced === "desktop") {
    return forced;
  }
  return mode;
}
function setPromoGradient(el: HTMLElement | null, gradient: string | undefined) {
  if (!el) {
    return;
  }
  if (gradient) {
    el.style.setProperty("--mmh-promo-gradient", gradient);
  } else {
    el.style.removeProperty("--mmh-promo-gradient");
  }
}
function resolveColumnMaxWidthPx(value: number | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 240;
  }
  return Math.min(480, Math.max(120, Math.round(value)));
}

/**
 * Compact capsule trigger text: prefer `UK · GBP`-style pairs from the label;
 * otherwise the short locale `code` (e.g. `UK`). Does not invent a currency.
 */
function formatLocaleCapsuleLabel(locale: {
  code: string;
  label: string;
}): string {
  const label = locale.label.trim();
  const pair = label.match(/^([A-Za-z]{2,4})\s*[-–—·/]\s*([A-Za-z]{2,4})$/);
  if (pair?.[1] && pair[2]) {
    return `${pair[1].toUpperCase()} · ${pair[2].toUpperCase()}`;
  }
  const code = locale.code.trim().toUpperCase();
  if (code.length > 0 && code.length <= 5) {
    return code;
  }
  const paren = label.match(/\(([A-Za-z]{2,5})\)\s*$/);
  if (paren?.[1]) {
    return paren[1].toUpperCase();
  }
  return label || code || "—";
}
function LangGlobeIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg aria-hidden={true} className={styles.langGlobe} fill="none" focusable="false" height="16" viewBox="0 0 24 24" width="16" xmlns="http://www.w3.org/2000/svg"><title>Globe</title><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" /><ellipse cx="12" cy="12" rx="4" ry="9" stroke="currentColor" strokeWidth="1.5" /><path d="M3.5 12h17M5.2 7.5h13.6M5.2 16.5h13.6" stroke="currentColor" strokeLinecap="round" strokeWidth="1.35" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Renders a sanitized, inline-persisted Iconify icon (nav items, group titles, links). */
function IconSpan(t0) {
  const $ = _c(20);
  const {
    svgMarkup,
    className,
    color,
    sizePx
  } = t0;
  let t1;
  if ($[0] !== svgMarkup) {
    t1 = svgMarkup?.trim() ? sanitizeIconifySvgMarkup(svgMarkup) : null;
    $[0] = svgMarkup;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const safe = t1;
  if (!safe) {
    return null;
  }
  let t2;
  if ($[2] !== sizePx) {
    t2 = typeof sizePx === "number" && Number.isFinite(sizePx) && sizePx > 0;
    $[2] = sizePx;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const sized = t2;
  let t3;
  if ($[4] !== color) {
    t3 = color?.trim();
    $[4] = color;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const tint = t3;
  const t4 = className ?? styles.navIcon;
  let t5;
  if ($[6] !== safe) {
    t5 = {
      __html: safe
    };
    $[6] = safe;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== sizePx || $[9] !== sized) {
    t6 = sized ? {
      width: sizePx,
      height: sizePx
    } : {};
    $[8] = sizePx;
    $[9] = sized;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== tint) {
    t7 = tint ? {
      color: tint
    } : {};
    $[11] = tint;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = {
      ...t6,
      ...t7
    };
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t4 || $[17] !== t5 || $[18] !== t8) {
    t9 = <span aria-hidden={true} className={t4} dangerouslySetInnerHTML={t5} style={t8} />;
    $[16] = t4;
    $[17] = t5;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}

/** Left/right icon-flanked label content for the Contact / Get-Started action links. */
function ActionLinkContent(t0) {
  const $ = _c(10);
  const {
    link
  } = t0;
  let t1;
  if ($[0] !== link.iconLeftSvgMarkup) {
    t1 = <IconSpan className={styles.actionIcon} svgMarkup={link.iconLeftSvgMarkup} />;
    $[0] = link.iconLeftSvgMarkup;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== link.label) {
    t2 = <span>{link.label}</span>;
    $[2] = link.label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== link.iconRightSvgMarkup) {
    t3 = <IconSpan className={styles.actionIcon} svgMarkup={link.iconRightSvgMarkup} />;
    $[4] = link.iconRightSvgMarkup;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2 || $[8] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  return t4;
}
function PromoCardCtas(t0) {
  const $ = _c(4);
  const {
    ctas
  } = t0;
  if (ctas.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== ctas) {
    t1 = ctas.map(_temp);
    $[0] = ctas;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className={styles.promoCtaRow}>{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp(cta) {
  return <a className={`${styles.promoCtaBtn} ${cta.style === "secondary" ? styles.promoCtaBtnSecondary : ""} ${cta.style === "link" ? styles.promoCtaBtnLink : ""}`} href={cta.href || "#"} key={cta.id}>{cta.label}</a>;
}
function PromoCard(t0) {
  const $ = _c(39);
  const {
    card,
    columnClassName
  } = t0;
  const htmlMode = card.contentMode === "html";
  if (htmlMode) {
    let t1;
    if ($[0] !== card.html) {
      t1 = card.html?.trim() ?? "";
      $[0] = card.html;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    const html = t1;
    if (!html) {
      return null;
    }
    const t2 = `${styles.promo} ${styles.promoHtml} ${columnClassName ?? ""}`;
    let t3;
    if ($[2] !== html) {
      t3 = {
        __html: html
      };
      $[2] = html;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== t2 || $[5] !== t3) {
      t4 = <div className={t2} dangerouslySetInnerHTML={t3} />;
      $[4] = t2;
      $[5] = t3;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    return t4;
  }
  let t1;
  if ($[7] !== card.imageUrl) {
    t1 = card.imageUrl?.trim();
    $[7] = card.imageUrl;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const imageUrl = t1;
  let t2;
  if ($[9] !== card.imageAlt || $[10] !== imageUrl) {
    t2 = imageUrl ? <img alt={card.imageAlt ?? ""} className={styles.promoImage} src={imageUrl} /> : null;
    $[9] = card.imageAlt;
    $[10] = imageUrl;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div aria-hidden={true} className={styles.promoBg} />;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] !== card.tag) {
    t4 = <span className={styles.promoTag}>{card.tag}</span>;
    $[13] = card.tag;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== card.title) {
    t5 = <p className={styles.promoTitle}>{card.title}</p>;
    $[15] = card.title;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== card.ctas) {
    t6 = card.ctas.length > 0 ? <PromoCardCtas ctas={card.ctas} /> : <div className={styles.promoCta}><span>Learn more</span><span aria-hidden={true}>→</span></div>;
    $[17] = card.ctas;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== t5 || $[20] !== t6) {
    t7 = <div>{t5}{t6}</div>;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t2 || $[23] !== t4 || $[24] !== t7) {
    t8 = <>{t2}{t3}{t4}{t7}</>;
    $[22] = t2;
    $[23] = t4;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const body = t8;
  if (card.ctas.length > 0) {
    const t9 = `${styles.promo} ${columnClassName ?? ""}`;
    let t10;
    if ($[26] !== card.gradient) {
      t10 = el => setPromoGradient(el, card.gradient);
      $[26] = card.gradient;
      $[27] = t10;
    } else {
      t10 = $[27];
    }
    let t11;
    if ($[28] !== body || $[29] !== t10 || $[30] !== t9) {
      t11 = <div className={t9} ref={t10}>{body}</div>;
      $[28] = body;
      $[29] = t10;
      $[30] = t9;
      $[31] = t11;
    } else {
      t11 = $[31];
    }
    return t11;
  }
  const t9 = `${styles.promo} ${columnClassName ?? ""}`;
  const t10 = card.href || "#";
  let t11;
  if ($[32] !== card.gradient) {
    t11 = el_0 => setPromoGradient(el_0, card.gradient);
    $[32] = card.gradient;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== body || $[35] !== t10 || $[36] !== t11 || $[37] !== t9) {
    t12 = <a className={t9} href={t10} ref={t11}>{body}</a>;
    $[34] = body;
    $[35] = t10;
    $[36] = t11;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
}

/** Returns clamped px, or undefined when unset/0 (no content-width constraint). */
function resolvePanelContentMaxWidthPx(value: number | undefined): number | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return undefined;
  }
  const rounded = Math.round(value);
  if (rounded <= 0) {
    return undefined;
  }
  return Math.min(1920, Math.max(480, rounded));
}
function MegaPanelColumns({
  root,
  promoCards,
  footerRow,
  navIcons,
  columnClassName,
  gridClassName,
  centerColumns = true,
  columnMaxWidthPx = 240
}: {
  root: MegaTabRoot;
  promoCards: AdvancedMegaMenuPromoCard[] | undefined;
  footerRow?: AdvancedMegaMenuHeaderRuntimePayload["footerRows"][string];
  navIcons: Record<string, AdvancedMegaMenuNavIcon>;
  columnClassName?: string;
  gridClassName?: string;
  centerColumns?: boolean;
  columnMaxWidthPx?: number;
}) {
  const columnCount = resolveColumnCount(root);
  const cards = promoCards ?? [];
  const showPromo = cards.length > 0;
  const footerLinks = footerRow != null && footerRow.enabled !== false ? footerRow.links.filter(l => l.label.trim().length > 0) : [];
  const showFooter = footerLinks.length > 0;
  const maxWidth = resolveColumnMaxWidthPx(columnMaxWidthPx);
  const centered = centerColumns !== false;
  const promoTrack = Math.round(maxWidth * 1.3);
  const isDrawer = gridClassName != null;
  const gridStyle = gridClassName == null ? {
    ...(centered ? {
      gridTemplateColumns: showPromo ? `repeat(${columnCount}, minmax(0, ${maxWidth}px)) ${promoTrack}px` : `repeat(${columnCount}, minmax(0, ${maxWidth}px))`,
      justifyContent: "center" as const
    } : {
      gridTemplateColumns: showPromo ? `repeat(${columnCount}, minmax(0, 1fr)) minmax(${promoTrack}px, 1.3fr)` : `repeat(${columnCount}, minmax(0, 1fr))`
    }),
    ["--mmh-col-max-width" as string]: `${maxWidth}px`
  } : undefined;
  const columns = <div className={gridClassName ?? `${styles.megaInner}${centered ? "" : ` ${styles.megaInnerStretch}`}`} data-mmh-cols={columnCount} style={gridStyle}>
			{groupsByColumn(root.groups, columnCount).map((colGroups, colIndex) => <div className={columnClassName ?? styles.col} key={`${root.id}-col-${colIndex}`}>
					{colGroups.map((group, groupIndex) => {
        const groupHref = group.href?.trim();
        const linked = Boolean(groupHref) && groupHref !== "#" && groupHref !== "/#";
        const groupIcon = navIcons[group.id];
        return <div className={styles.group} key={group.id || `${root.id}-group-${groupIndex}`}>
								{linked && groupHref ? <a className={styles.groupTitle} href={groupHref}>
										<IconSpan color={groupIcon?.color} sizePx={groupIcon?.sizePx} svgMarkup={groupIcon?.svgMarkup} />
										{group.label}
									</a> : <p className={styles.groupTitle}>
										<IconSpan color={groupIcon?.color} sizePx={groupIcon?.sizePx} svgMarkup={groupIcon?.svgMarkup} />
										{group.label}
									</p>}
								<ul className={styles.linkList}>
									{group.links.map((link, linkIndex) => {
              const description = link.description?.trim();
              const linkIcon = navIcons[link.id];
              return <li className={styles.linkItem} key={link.id || `${group.id || groupIndex}-link-${linkIndex}`}>
												<a className={styles.link} href={link.href} rel={link.openInNewTab ? "noopener noreferrer" : undefined} target={link.openInNewTab ? "_blank" : undefined}>
													<IconSpan color={linkIcon?.color} sizePx={linkIcon?.sizePx} svgMarkup={linkIcon?.svgMarkup} />
													<span className={styles.linkBody}>
														<span className={styles.linkText}>
															{link.label}
														</span>
														{description ? <span className={styles.linkDescription}>
																{description}
															</span> : null}
													</span>
													<span aria-hidden className={styles.linkArrow}>
														→
													</span>
												</a>
											</li>;
            })}
								</ul>
							</div>;
      })}
				</div>)}
			{showPromo ? <div className={`${styles.promoStack} ${columnClassName ?? ""}`}>
					{cards.map(card => <PromoCard card={card} key={card.id} />)}
				</div> : null}
		</div>;
  const footer = showFooter ? <div className={isDrawer ? styles.drawerFooterRow : styles.megaFooterRow}>
			{footerRow?.label.trim() ? <p className={styles.megaFooterLabel}>{footerRow.label}</p> : null}
			<ul className={styles.megaFooterPills}>
				{footerLinks.map(link_0 => <li key={link_0.id || `${link_0.label}-${link_0.href}`}>
						<a className={styles.megaFooterPill} href={link_0.href || "#"}>
							{link_0.label}
						</a>
					</li>)}
			</ul>
		</div> : null;
  if (isDrawer) {
    return <div className={styles.drawerMegaStack}>
				{columns}
				{footer}
			</div>;
  }
  return <div className={styles.megaPanelContent}>
			{columns}
			{footer}
		</div>;
}
export function AdvancedMegaMenuHeader({
  payload,
  afterLogo,
  drawerLead,
  previewMode = false,
  currentPathname = null,
  onActiveMegaTabChange,
  forcedViewport = null
}: {
  payload: AdvancedMegaMenuHeaderRuntimePayload;
  /** Optional chrome between logo and primary nav. */
  afterLogo?: ReactNode;
  /** Optional block under the mobile drawer title. */
  drawerLead?: ReactNode;
  /** Builder canvas: block navigation so header links do not leave the editor. */
  previewMode?: boolean;
  /** Public request pathname — SSR-correct language when URL matches a locale. */
  currentPathname?: string | null;
  /**
   * Fired when a mega panel tab is open/closed (label stable across audience menus).
   */
  onActiveMegaTabChange?: (tab: {
    id: string;
    label: string;
  } | null) => void;
  /** Builder device toolbar: force mobile/tablet chrome instead of window width. */
  forcedViewport?: ViewportMode | null;
}) {
  const viewport = useViewportMode(forcedViewport);
  const isCompact = viewport !== "desktop";
  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMegaTabId, setExpandedMegaTabId] = useState<string | null>(null);
  // Tracks every drawer tab opened at least once — a never-opened tab's panel
  // (including its promo cards' custom-code) is not rendered at all, so it can't
  // be serialized into the initial RSC payload. Only grows; a collapsed-again
  // tab stays mounted so the collapse animation still plays.
  const [openedMegaTabIds, setOpenedMegaTabIds] = useState<Set<string>>(() => new Set());
  const [langOpen, setLangOpen] = useState(false);
  const pathLocaleCode = useMemo(() => resolveLocaleCodeFromPath(payload.languageSwitcher, currentPathname), [payload.languageSwitcher, currentPathname]);
  const [langCode, setLangCode] = useState(pathLocaleCode);
  useEffect(() => {
    setLangCode(pathLocaleCode);
  }, [pathLocaleCode]);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const langCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navRef = useRef<HTMLElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  const menuToggleRef = useRef<HTMLButtonElement>(null);
  /** Label of the open mega tab — rematch when audience menuId swaps. */
  const activeMegaLabelRef = useRef<string | null>(null);
  const currentLocale = payload.languageSwitcher.locales.find(l => l.code === langCode) ?? payload.languageSwitcher.locales[0];
  const capsuleLabel = currentLocale ? formatLocaleCapsuleLabel(currentLocale) : langCode;
  const movePill = useCallback((key: string | null) => {
    const pill = pillRef.current;
    const nav = navRef.current;
    if (!(pill && nav && key)) {
      if (pill) {
        pill.style.opacity = "0";
      }
      return;
    }
    const btn = nav.querySelector(`[data-nav-key="${key}"]`);
    if (!btn) {
      return;
    }
    const nr = nav.getBoundingClientRect();
    const br = btn.getBoundingClientRect();
    pill.style.left = `${br.left - nr.left}px`;
    pill.style.width = `${br.width}px`;
    pill.style.opacity = "1";
  }, []);
  const highlightNav = useCallback((key_0: string) => {
    if (isCompact) {
      return;
    }
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    const root = payload.roots.find(r => r.id === key_0);
    if (root?.itemKind === "mega_tab") {
      activeMegaLabelRef.current = root.label.trim() || null;
    } else {
      activeMegaLabelRef.current = null;
    }
    setActiveTab(key_0);
    movePill(key_0);
  }, [isCompact, movePill, payload.roots]);
  const closeMenu = useCallback(() => {
    if (isCompact) {
      return;
    }
    closeTimer.current = setTimeout(() => {
      activeMegaLabelRef.current = null;
      setActiveTab(null);
      movePill(null);
    }, payload.closeDelayMs);
  }, [isCompact, movePill, payload.closeDelayMs]);
  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);
  const openLangMenu = useCallback(() => {
    if (langCloseTimer.current) {
      clearTimeout(langCloseTimer.current);
      langCloseTimer.current = null;
    }
    setLangOpen(true);
  }, []);
  const scheduleCloseLangMenu = useCallback(() => {
    if (langCloseTimer.current) {
      clearTimeout(langCloseTimer.current);
    }
    langCloseTimer.current = setTimeout(() => {
      setLangOpen(false);
      langCloseTimer.current = null;
    }, 220);
  }, []);
  const closeLangMenuNow = useCallback(() => {
    if (langCloseTimer.current) {
      clearTimeout(langCloseTimer.current);
      langCloseTimer.current = null;
    }
    setLangOpen(false);
  }, []);
  const selectLocale = useCallback((loc: (typeof payload.languageSwitcher.locales)[number]) => {
    if (isLocaleCurrentForPath(loc, currentPathname)) {
      return;
    }
    setLangCode(loc.code);
    closeLangMenuNow();
    const href = loc.href?.trim();
    if (!(href && !previewMode && href !== "#")) {
      return;
    }
    window.location.assign(href);
  }, [closeLangMenuNow, currentPathname, previewMode]);
  useEffect(() => {
    return () => {
      if (langCloseTimer.current) {
        clearTimeout(langCloseTimer.current);
      }
    };
  }, []);
  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
    setExpandedMegaTabId(null);
    closeLangMenuNow();
    menuToggleRef.current?.focus();
  }, [closeLangMenuNow]);
  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(open => {
      if (open) {
        setExpandedMegaTabId(null);
        closeLangMenuNow();
      }
      return !open;
    });
  }, [closeLangMenuNow]);
  const toggleMegaAccordion = useCallback((tabId: string) => {
    setExpandedMegaTabId(current => current === tabId ? null : tabId);
    setOpenedMegaTabIds(current_0 => current_0.has(tabId) ? current_0 : new Set(current_0).add(tabId));
  }, []);
  useEffect(() => {
    if (!isCompact) {
      setMobileMenuOpen(false);
      setExpandedMegaTabId(null);
    }
  }, [isCompact]);

  // Rematch open mega by label when audience menuId swaps, or restore after hub nav.
  useLayoutEffect(() => {
    setExpandedMegaTabId(null);
    const sessionLabel = consumeOpenMegaTabLabel();
    const label = (sessionLabel ?? activeMegaLabelRef.current)?.trim() || null;
    if (label) {
      const match = findMegaTabByLabel(payload.roots, label);
      if (match) {
        activeMegaLabelRef.current = match.label.trim() || null;
        setActiveTab(match.id);
        movePill(match.id);
        return;
      }
    }
    // Drop highlight when the previous tab id vanished after an audience swap.
    setActiveTab(prev => {
      if (!prev) {
        return null;
      }
      const stillThere = payload.roots.some(r_0 => r_0.id === prev);
      if (stillThere) {
        return prev;
      }
      activeMegaLabelRef.current = null;
      movePill(null);
      return null;
    });
  }, [payload.menuId, payload.roots, movePill]);
  useEffect(() => {
    if (!onActiveMegaTabChange) {
      return;
    }
    const root_0 = payload.roots.find(r_1 => r_1.itemKind === "mega_tab" && r_1.id === activeTab);
    if (root_0?.itemKind === "mega_tab") {
      onActiveMegaTabChange({
        id: root_0.id,
        label: root_0.label.trim()
      });
      return;
    }
    onActiveMegaTabChange(null);
  }, [activeTab, onActiveMegaTabChange, payload.roots]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") {
        return;
      }
      if (mobileMenuOpen) {
        closeMobileMenu();
        return;
      }
      activeMegaLabelRef.current = null;
      setActiveTab(null);
      closeLangMenuNow();
      movePill(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeLangMenuNow, closeMobileMenu, mobileMenuOpen, movePill]);
  useEffect(() => {
    if (!(mobileMenuOpen && isCompact)) {
      return;
    }
    const prev_0 = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev_0;
    };
  }, [mobileMenuOpen, isCompact]);
  useEffect(() => {
    if (!(mobileMenuOpen && isCompact)) {
      return;
    }
    const drawer = drawerRef.current;
    if (!drawer) {
      return;
    }
    const focusables = drawer.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])');
    if (focusables.length === 0) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    first.focus();
    const onKeyDown = (e_0: KeyboardEvent) => {
      if (e_0.key !== "Tab") {
        return;
      }
      if (e_0.shiftKey) {
        if (document.activeElement === first) {
          e_0.preventDefault();
          last.focus();
        }
        return;
      }
      if (document.activeElement === last) {
        e_0.preventDefault();
        first.focus();
      }
    };
    drawer.addEventListener("keydown", onKeyDown);
    return () => drawer.removeEventListener("keydown", onKeyDown);
  }, [mobileMenuOpen, isCompact]);
  const activeRoot = payload.roots.find(r_2 => r_2.itemKind === "mega_tab" && r_2.id === activeTab);
  const promos = payload.promos ?? {};
  const footerRows = payload.footerRows ?? {};
  const navIcons = payload.navIcons ?? {};
  const activePromoCards = activeRoot && activeRoot.itemKind === "mega_tab" ? promos[activeRoot.id] : undefined;
  const activeFooter = activeRoot && activeRoot.itemKind === "mega_tab" ? footerRows[activeRoot.id] : undefined;
  const megaOpen = !isCompact && activeTab != null && activeRoot?.itemKind === "mega_tab";
  const navChromeOpen = !isCompact && activeTab != null;
  const logoHref = safeLogoHref(payload.logoHref, payload.logoUrl);
  const logoSrc = payload.logoUrl?.trim();
  const logoOpenSrc = payload.logoOpenUrl?.trim();
  const showOpenLogo = Boolean(navChromeOpen) && Boolean(logoOpenSrc) && logoOpenSrc !== logoSrc;
  const logoIntrinsic = imgElementIntrinsicProps(payload.logoWidth, payload.logoHeight);
  const logoWidth = logoIntrinsic.width ?? 180;
  const logoHeight = logoIntrinsic.height ?? 36;
  const rootRef = useRef<HTMLDivElement>(null);
  const stickyUi = useMegaMenuStickyHeader({
    enabled: payload.sticky === true,
    lockVisible: megaOpen || mobileMenuOpen || langOpen,
    mode: payload.stickyMode === "smart" ? "smart" : "always",
    previewMode,
    rootRef
  });

  /** Layout-critical: must be on first paint (SSR + hydration), not useEffect. */
  const panelContentMaxPx = resolvePanelContentMaxWidthPx(payload.design.panelContentMaxWidthPx);
  const logoDisplayHeightPx = resolveMegaMenuLogoDisplayHeightPx(payload.logoHeightPx, payload.logoSizeVariant);
  const logoRasterMaxPx = resolveMegaMenuLogoRasterMaxPx(logoDisplayHeightPx, payload.logoSizeVariant);
  const logoSizes = payload.logoSizes?.trim() || `(max-width: 767px) min(50vw, 140px), ${logoRasterMaxPx}px`;
  const rootLayoutStyle = {
    ["--mmh-col-max-width" as string]: `${resolveColumnMaxWidthPx(payload.design.columnMaxWidthPx)}px`,
    ...(panelContentMaxPx == null ? {} : {
      ["--mmh-panel-content-max" as string]: `${panelContentMaxPx}px`
    }),
    ...(logoDisplayHeightPx == null ? {} : {
      ["--mmh-logo-height" as string]: `${logoDisplayHeightPx}px`
    })
  };
  useEffect(() => {
    const root_1 = rootRef.current;
    if (!root_1) {
      return;
    }
    // Colors: cosmetic, fine after paint.
    applyAdvancedMegaMenuDesignCssVars(root_1, payload.design);
  }, [payload.design.bubbleBackground, payload.design.pillColor, payload.design.textColor, payload.design.navActiveTextColor, payload.design.navInactiveOpenTextColor, payload.design.categoryColor, payload.design.categoryHoverColor, payload.design.linkColor, payload.design.linkDescriptionColor, payload.design.linkHoverColor, payload.design.linkHoverBackground, payload.design.navIconColor, payload.design.footerLabelColor, payload.design.footerPillTextColor, payload.design.footerPillBackground, payload.design.footerPillBorderColor, payload.design.footerPillHoverBackground, payload.design.footerPillHoverTextColor, payload.design.ctaBackground, payload.design.ctaTextColor, payload.design.ctaHoverBackground, payload.design.promoImageOverlayColor, payload.design.promoCtaBackground, payload.design.promoCtaTextColor, payload.design.promoCtaHoverBackground, payload.design.audienceToggleBorderColor, payload.design.audienceToggleActiveBackground, payload.design.audienceToggleActiveTextColor, payload.design.audienceToggleInactiveBackground, payload.design.audienceToggleInactiveTextColor, payload.design.audienceToggleInactiveHoverBackground]);
  const renderLangOptions = (listboxId: string) => <div aria-label="Languages" className={styles.langDd} id={listboxId} role="listbox">
			{payload.languageSwitcher.locales.map(loc_0 => {
      const isCurrent = isLocaleCurrentForPath(loc_0, currentPathname);
      const isSelected = loc_0.code === langCode;
      return <div className={styles.langOptionWrap} key={loc_0.code}>
						<button {...isSelected ? {
          "aria-selected": "true"
        } : {
          "aria-selected": "false"
        }} aria-current={isCurrent ? "true" : undefined} aria-disabled={isCurrent ? "true" : undefined} className={`${styles.langItem} ${isCurrent ? styles.langItemCurrent : ""}`} disabled={isCurrent} onClick={() => selectLocale(loc_0)} role="option" type="button">
							<span aria-hidden className={styles.langItemFlag}>
								{loc_0.flag}
							</span>
							<span>{loc_0.label}</span>
							{isCurrent ? <span className={styles.langItemCurrentHint}>Current</span> : null}
						</button>
					</div>;
    })}
		</div>;
  const renderDrawerActions = (inFooter: boolean) => <div className={inFooter ? styles.drawerFooterActions : styles.drawerInlineActions}>
			{payload.languageSwitcher.enabled ? <div className={`${styles.langWrap} ${langOpen ? styles.langWrapOpen : ""}`}>
					<button {...langOpen ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} aria-controls="ammh-lang-listbox-drawer" aria-haspopup="listbox" aria-label={`Language: ${capsuleLabel}`} className={styles.langBtn} onClick={() => setLangOpen(o => !o)} type="button">
						<LangGlobeIcon />
						<span className={styles.langBtnText}>{capsuleLabel}</span>
					</button>
					{renderLangOptions("ammh-lang-listbox-drawer")}
				</div> : null}
			{payload.contact.enabled ? <a className={styles.contact} href={payload.contact.href}>
					<ActionLinkContent link={payload.contact} />
				</a> : null}
			{payload.cta.enabled ? <a className={styles.ctaWrap} href={payload.cta.href}>
					<span aria-hidden className={styles.ctaSwipe} />
					<span className={styles.ctaLabel}>
						<ActionLinkContent link={payload.cta} />
					</span>
				</a> : null}
		</div>;
  const blockPreviewLinkNav = useCallback((e_1: ReactMouseEvent) => {
    if (!previewMode) {
      return;
    }
    const target = e_1.target;
    if (!(target instanceof Element)) {
      return;
    }
    const anchor = target.closest("a[href]");
    if (anchor) {
      e_1.preventDefault();
      // Keep bubbling so the builder canvas wrapper can select this element.
    }
  }, [previewMode]);
  if (isReplicaChrome(payload)) {
    return <AdvancedMegaMenuReplicaChrome afterLogo={afterLogo} currentPathname={currentPathname} drawerLead={drawerLead} forcedViewport={forcedViewport} payload={payload} previewMode={previewMode} />;
  }
  return <div className={[styles.root, payload.sticky === true ? styles.rootSticky : "", stickyUi.stuck ? styles.rootStuck : "", stickyUi.retracted ? styles.rootRetracted : ""].filter(Boolean).join(" ")} data-pb-advanced-mega-menu="" data-sticky={payload.sticky === true ? "true" : "false"} data-viewport={viewport} onClickCapture={previewMode ? blockPreviewLinkNav : undefined} onMouseLeave={isCompact ? undefined : closeMenu} ref={rootRef} style={rootLayoutStyle}>
			<div className={`${styles.bubble} ${navChromeOpen ? styles.bubbleOpen : ""} ${megaOpen ? styles.bubbleMegaOpen : ""}`}>
				<div className={styles.topbar}>
					<div className={styles.topbarInner}>
						<div className={styles.brandCluster}>
							<a className={styles.logo} href={logoHref}>
								{logoSrc ? <span className={styles.logoImgWrap}>
										<img alt={showOpenLogo && logoOpenSrc ? "" : payload.logoAlt || ""} aria-hidden={showOpenLogo ? true : undefined} className={`${styles.logoImg} ${showOpenLogo ? styles.logoImgFaded : ""}`} height={logoHeight} sizes={logoSizes} src={logoSrc} srcSet={payload.logoSrcSet ?? undefined} width={logoWidth} />
										{logoOpenSrc ? <img alt={payload.logoAlt || ""} aria-hidden={showOpenLogo ? undefined : true} className={`${styles.logoImg} ${styles.logoImgOverlay} ${showOpenLogo ? styles.logoImgOverlayVisible : ""}`} height={logoHeight} sizes={logoSizes} src={logoOpenSrc} width={logoWidth} /> : null}
									</span> : <span>{payload.logoAlt || "Logo"}</span>}
							</a>
							{afterLogo}
						</div>

						<nav aria-label="Primary" className={styles.navDesktop} ref={navRef}>
							<span aria-hidden className={styles.pill} ref={pillRef} />
							{payload.roots.map((root_2, rootIndex) => {
              const key_1 = menuRootKey(root_2, rootIndex);
              if (root_2.itemKind === "link") {
                const navKey = root_2.id || key_1;
                const isActive = activeTab === navKey;
                return <a className={`${styles.navBtn} ${isActive ? styles.navBtnActive : ""}`} data-nav-key={navKey} href={root_2.href} key={key_1} onFocus={() => highlightNav(navKey)} onMouseEnter={() => highlightNav(navKey)} rel={root_2.openInNewTab ? "noopener noreferrer" : undefined} target={root_2.openInNewTab ? "_blank" : undefined}>
											<IconSpan color={navIcons[root_2.id]?.color} sizePx={navIcons[root_2.id]?.sizePx} svgMarkup={navIcons[root_2.id]?.svgMarkup} />
											{root_2.label}
										</a>;
              }
              if (root_2.itemKind !== "mega_tab") {
                return null;
              }
              const isActive_0 = activeTab === root_2.id;
              const hasMegaPanel = megaTabHasPanelContent(root_2, promos, footerRows);
              return <button key={key_1} {...hasMegaPanel ? {
                ...(isActive_0 ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE),
                "aria-haspopup": "menu"
              } : {}} className={`${styles.navBtn} ${isActive_0 ? styles.navBtnActive : ""}`} data-nav-key={root_2.id} onFocus={() => highlightNav(root_2.id)} onMouseEnter={() => highlightNav(root_2.id)} type="button">
										<IconSpan color={navIcons[root_2.id]?.color} sizePx={navIcons[root_2.id]?.sizePx} svgMarkup={navIcons[root_2.id]?.svgMarkup} />
										<span className={styles.navBtnLabel}>{root_2.label}</span>
										{hasMegaPanel ? <span aria-hidden className={styles.navChevron}>
												▾
											</span> : null}
									</button>;
            })}
						</nav>

						<div className={styles.rightDesktop}>
							{payload.languageSwitcher.enabled ? <div className={`${styles.langWrap} ${langOpen ? styles.langWrapOpen : ""}`} onMouseEnter={openLangMenu} onMouseLeave={scheduleCloseLangMenu}>
									<button {...langOpen ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} aria-controls="ammh-lang-listbox-desktop" aria-haspopup="listbox" aria-label={`Language: ${capsuleLabel}`} className={styles.langBtn} onFocus={openLangMenu} type="button">
										<LangGlobeIcon />
										<span className={styles.langBtnText}>{capsuleLabel}</span>
									</button>
									{renderLangOptions("ammh-lang-listbox-desktop")}
								</div> : null}
							{payload.contact.enabled ? <a className={styles.contact} href={payload.contact.href}>
									<ActionLinkContent link={payload.contact} />
								</a> : null}
							{payload.cta.enabled ? <a className={styles.ctaWrap} href={payload.cta.href}>
									<span aria-hidden className={styles.ctaSwipe} />
									<span className={styles.ctaLabel}>
										<ActionLinkContent link={payload.cta} />
									</span>
								</a> : null}
						</div>

						<button aria-controls="ammh-mobile-drawer" {...mobileMenuOpen ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} aria-label={mobileMenuOpen ? "Close menu" : "Open menu"} className={styles.menuToggle} onClick={toggleMobileMenu} ref={menuToggleRef} type="button">
							<span className={`${styles.menuToggleBar} ${mobileMenuOpen ? styles.menuToggleBarOpen : ""}`} />
							<span className={`${styles.menuToggleBar} ${mobileMenuOpen ? styles.menuToggleBarOpenMid : ""}`} />
							<span className={`${styles.menuToggleBar} ${mobileMenuOpen ? styles.menuToggleBarOpenBot : ""}`} />
						</button>
					</div>
				</div>

				{megaOpen && activeRoot?.itemKind === "mega_tab" ? <div className={`${styles.mega} ${styles.megaDesktop} ${styles.megaVisible}`} onMouseEnter={cancelClose} onMouseLeave={closeMenu}>
						<MegaPanelColumns centerColumns={payload.design.centerColumns !== false} columnMaxWidthPx={payload.design.columnMaxWidthPx} footerRow={activeFooter} navIcons={navIcons} promoCards={activePromoCards} root={activeRoot} />
					</div> : null}
			</div>

			<button {...mobileMenuOpen ? ARIA_HIDDEN_FALSE : ARIA_HIDDEN_TRUE} aria-label="Close menu" className={`${styles.drawerBackdrop} ${mobileMenuOpen ? styles.drawerBackdropVisible : ""}`} onClick={closeMobileMenu} tabIndex={mobileMenuOpen ? 0 : -1} type="button" />

			<aside {...mobileMenuOpen ? ARIA_HIDDEN_FALSE : ARIA_HIDDEN_TRUE} className={`${styles.drawer} ${mobileMenuOpen ? styles.drawerOpen : ""}`} id="ammh-mobile-drawer" ref={drawerRef}>
				<div className={styles.drawerHeader}>
					<span className={styles.drawerTitle}>Menu</span>
					<button aria-label="Close menu" className={styles.drawerClose} onClick={closeMobileMenu} type="button">
						×
					</button>
				</div>

				{drawerLead ? <div className={styles.drawerLead}>{drawerLead}</div> : null}

				<nav aria-label="Mobile primary" className={styles.drawerNav}>
					<ul className={styles.drawerNavList}>
						{payload.roots.map((root_3, rootIndex_0) => {
            const key_2 = menuRootKey(root_3, rootIndex_0);
            if (root_3.itemKind === "link") {
              return <li className={styles.drawerNavItem} key={key_2}>
										<a className={styles.drawerLink} href={root_3.href} onClick={closeMobileMenu} rel={root_3.openInNewTab ? "noopener noreferrer" : undefined} target={root_3.openInNewTab ? "_blank" : undefined}>
											<IconSpan color={navIcons[root_3.id]?.color} sizePx={navIcons[root_3.id]?.sizePx} svgMarkup={navIcons[root_3.id]?.svgMarkup} />
											{root_3.label}
										</a>
									</li>;
            }
            if (root_3.itemKind !== "mega_tab") {
              return null;
            }
            const expanded = expandedMegaTabId === root_3.id;
            const tabPromoCards = promos[root_3.id];
            const tabFooter = footerRows[root_3.id];
            return <li className={styles.drawerNavItem} key={key_2}>
									<button aria-controls={`ammh-accordion-${root_3.id}`} {...expanded ? ARIA_EXPANDED_TRUE : ARIA_EXPANDED_FALSE} className={styles.drawerAccordionBtn} onClick={() => toggleMegaAccordion(root_3.id)} type="button">
										<span className={styles.drawerAccordionBtnLabel}>
											<IconSpan color={navIcons[root_3.id]?.color} sizePx={navIcons[root_3.id]?.sizePx} svgMarkup={navIcons[root_3.id]?.svgMarkup} />
											{root_3.label}
										</span>
										<span aria-hidden className={`${styles.drawerChevron} ${expanded ? styles.drawerChevronOpen : ""}`}>
											▾
										</span>
									</button>
									<div className={`${styles.drawerAccordionPanel} ${expanded ? styles.drawerAccordionPanelOpen : ""}`} hidden={!expanded} id={`ammh-accordion-${root_3.id}`}>
										{openedMegaTabIds.has(root_3.id) ? <MegaPanelColumns centerColumns={payload.design.centerColumns !== false} columnClassName={styles.drawerCol} columnMaxWidthPx={payload.design.columnMaxWidthPx} footerRow={tabFooter} gridClassName={viewport === "tablet" ? styles.drawerMegaGridTablet : styles.drawerMegaGridMobile} navIcons={navIcons} promoCards={tabPromoCards} root={root_3} /> : null}
									</div>
								</li>;
          })}
					</ul>
				</nav>

				<footer className={styles.drawerFooter}>
					{renderDrawerActions(true)}
				</footer>
			</aside>
		</div>;
}